import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Video, ArrowUpRight, CalendarDays, X, Check, Loader2, Clock, Users } from "lucide-react";
import { TiltCard } from "@/components/motifs";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { isFutureEvent } from "@/lib/eventDate";
import { imgStyle } from "@/lib/imagePos";

export function RsvpModal({ ev, onClose }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", guests: 1 });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await api.post("/rsvps", { event_id: ev.id, event_title: ev.title, name: form.name, email: form.email, guests: Number(form.guests) || 1, lang });
      setState("done");
      toast.success(t("You're in! See you there.", "C'est confirmé! On s'y retrouve."));
    } catch (e2) {
      setState("idle");
      toast.error(formatApiError(e2.response?.data?.detail));
    }
  };

  const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2CACD4]";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl bg-card shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="rsvp-modal">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="font-display font-black uppercase text-lg leading-tight pr-4">{t("RSVP", "Je participe")} · {ev.title}</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-muted cursor-pointer" aria-label="Close"><X size={18} /></button>
        </div>
        {state === "done" ? (
          <div className="p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#CAE295] text-slate-900"><Check size={28} strokeWidth={3} /></span>
            <p className="mt-4 font-semibold">{t("You're on the list.", "Vous êtes inscrit.")}</p>
            <button onClick={onClose} className="mt-5 rounded-full bg-[#0F172A] text-white hover:bg-[#2CACD4] hover:text-slate-950 font-bold px-6 py-2.5 cursor-pointer transition-colors" data-testid="rsvp-close">{t("Done", "Terminé")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4" data-testid="rsvp-form">
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("Your name", "Votre nom")}</label>
              <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="rsvp-name" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("Email", "Courriel")}</label>
              <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="rsvp-email" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">{t("Number of people", "Nombre de personnes")}</label>
              <input type="number" min="1" value={form.guests} onChange={set("guests")} className={inputCls} data-testid="rsvp-guests" />
            </div>
            <button type="submit" disabled={state === "loading"} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#DB1A5C] text-white hover:bg-[#0F172A] font-bold px-6 py-3 transition-colors cursor-pointer disabled:opacity-60" data-testid="rsvp-submit">
              {state === "loading" ? <Loader2 className="animate-spin" size={18} /> : t("Confirm RSVP", "Confirmer")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function EventDetailModal({ ev, onClose, onRsvp }) {
  const { t } = useLang();
  const isVirtual = ev.format === "virtual";
  const cap = Number(ev.capacity || 0);
  const taken = Number(ev.rsvp_taken || 0);
  const soldOut = cap > 0 && taken >= cap;
  const past = !isFutureEvent(ev);
  const closed = soldOut || past;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={onClose}>
      <div className="w-full max-w-lg rounded-3xl bg-card shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="event-detail-modal">
        <div className="relative h-56">
          <img src={ev.image} alt={ev.image_alt || ev.title} className="h-full w-full object-cover" style={imgStyle(ev, "image")} />
          <button onClick={onClose} className="absolute top-3 right-3 rounded-full bg-white/90 p-2 hover:bg-white cursor-pointer" aria-label="Close" data-testid="event-detail-close"><X size={18} /></button>
          <span className="absolute bottom-3 left-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white shadow" style={{ background: ev.accent || "#2CACD4" }}>
            {isVirtual ? <Video size={13} /> : <MapPin size={13} />}{isVirtual ? t("Virtual", "Virtuel") : t("In-person", "En personne")}
          </span>
        </div>
        <div className="p-6">
          <p className="eyebrow" style={{ color: ev.accent || "#2CACD4" }}>{ev.season}</p>
          <h2 className="mt-2 font-display font-black uppercase text-2xl leading-tight">{ev.title}</h2>
          <div className="mt-4 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
            <p className="flex items-center gap-2"><CalendarDays size={15} /> {ev.date}</p>
            {ev.time && <p className="flex items-center gap-2"><Clock size={15} /> {ev.time}</p>}
            <p className="flex items-center gap-2">{isVirtual ? <Video size={15} /> : <MapPin size={15} />} {ev.location}</p>
            {ev.audience && <p className="flex items-center gap-2"><Users size={15} /> {ev.audience}</p>}
          </div>
          <p className="mt-4 text-foreground/80">{ev.blurb}</p>
          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={() => !closed && onRsvp()}
              disabled={closed}
              className={`inline-flex items-center gap-2 rounded-full font-bold px-6 py-3 transition-colors ${closed ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-[#0F172A] text-white hover:bg-[#DB1A5C] cursor-pointer"}`}
              data-testid="event-detail-rsvp"
            >
              {soldOut ? t("Sold out", "Complet") : past ? t("Registration closed", "Inscriptions fermées") : t("RSVP to this event", "S'inscrire à cet événement")}
            </button>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#5b8a2b" }}>{t("Free to join", "Gratuit")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EventCard({ ev, tilt = "", mt = "", testid }) {
  const { t } = useLang();
  const [rsvp, setRsvp] = useState(false);
  const [detail, setDetail] = useState(false);
  const isVirtual = ev.format === "virtual";
  const cap = Number(ev.capacity || 0);
  const taken = Number(ev.rsvp_taken || 0);
  const soldOut = cap > 0 && taken >= cap;
  const spotsLeft = cap - taken;
  const past = !isFutureEvent(ev);
  const disabled = soldOut || past;
  return (
    <>
      <TiltCard accent={ev.accent} tilt={tilt} mt={mt} testid={testid} className="overflow-hidden flex flex-col h-full">
        <div className="relative h-52 overflow-hidden">
          <img src={ev.image} alt={ev.image_alt || ev.title} className="h-full w-full object-cover" loading="lazy" style={imgStyle(ev, "image")} />
          <span
            className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white shadow-md"
            style={{ background: ev.accent || "#2CACD4" }}
          >
            {isVirtual ? <Video size={13} /> : <MapPin size={13} />}
            {isVirtual ? t("Virtual", "Virtuel") : t("In-person", "En personne")}
          </span>
          {soldOut && (
            <span className="absolute top-4 right-4 rounded-full bg-[#0F172A] px-3 py-1.5 text-xs font-black uppercase text-white shadow-md" data-testid={testid ? `${testid}-soldout` : "event-soldout"}>
              {t("Sold out", "Complet")}
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col p-6">
          <p className="eyebrow mb-2" style={{ color: ev.accent || "#2CACD4" }}>{ev.season}</p>
          <h3 className="font-display font-black uppercase text-xl leading-tight">{ev.title}</h3>
          <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
            <p className="flex items-center gap-2"><CalendarDays size={15} /> {ev.date}</p>
            <p className="flex items-center gap-2">{isVirtual ? <Video size={15} /> : <MapPin size={15} />} {ev.location}</p>
          </div>
          <p className="mt-3 text-sm text-foreground/70 line-clamp-3 flex-1">{ev.blurb}</p>
          {cap > 0 && !soldOut && spotsLeft <= 20 && (
            <p className="mt-3 text-xs font-bold uppercase tracking-wider text-[#DB1A5C]">{t(`${spotsLeft} spots left`, `${spotsLeft} places restantes`)}</p>
          )}
          <div className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4">
            <button
              onClick={() => !disabled && setRsvp(true)}
              disabled={disabled}
              className={`inline-flex items-center gap-1.5 rounded-full font-bold text-sm px-4 py-2 transition-colors ${disabled ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-[#0F172A] text-white hover:bg-[#DB1A5C] cursor-pointer"}`}
              data-testid={testid ? `${testid}-rsvp` : "event-rsvp"}
            >
              {soldOut ? t("Sold out", "Complet") : past ? t("Ended", "Terminé") : t("RSVP", "Je participe")}
            </button>
            <button onClick={() => setDetail(true)} className="inline-flex items-center gap-1 text-sm font-bold text-foreground hover:text-[#2CACD4] transition-colors cursor-pointer" data-testid={testid ? `${testid}-details` : "event-details"}>
              {t("Details", "Détails")} <ArrowUpRight size={15} />
            </button>
          </div>
        </div>
      </TiltCard>
      {detail && <EventDetailModal ev={ev} onClose={() => setDetail(false)} onRsvp={() => { setDetail(false); setRsvp(true); }} />}
      {rsvp && <RsvpModal ev={ev} onClose={() => setRsvp(false)} />}
    </>
  );
}
